/**
 * Mobile header (Reppr Elite mobile, v2 handoff).
 *
 * The handoff's phone frames carry a slim header: the Reppr mark, the screen
 * name, and a right-hand slot. The design puts a clock in that slot; this app
 * needs it for navigation — under 768px AppLayout renders neither the sidebar
 * nor AppHeader, so without this menu the screens outside the four tab-bar
 * destinations would be unreachable on a phone.
 *
 * The menu deliberately omits what the handoff lists as absent on mobile
 * (registers, create/edit forms, settings-level admin) only where the app has
 * no mobile-usable version; everything the user can still act on is listed.
 */

import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from './MobileHeader.module.css'

const SCREEN_TITLES: { match: RegExp; title: string }[] = [
  { match: /^\/dashboard/, title: 'Needs you' },
  { match: /^\/needs-you/, title: 'Needs you' },
  { match: /^\/outreach/, title: 'Outreach' },
  { match: /^\/portal-access/, title: 'Portal access' },
  { match: /^\/audience/, title: 'Audience' },
  { match: /^\/signals/, title: 'Signals' },
  { match: /^\/clients\/[^/]+/, title: 'Athlete' },
  { match: /^\/clients/, title: 'The Book' },
  { match: /^\/deals\/[^/]+/, title: 'Deal' },
  { match: /^\/deals/, title: 'Pipeline' },
  { match: /^\/contracts/, title: 'Contracts' },
  { match: /^\/compliance/, title: 'Compliance' },
  { match: /^\/tasks/, title: 'Tasks' },
  { match: /^\/calendar/, title: 'Calendar' },
  { match: /^\/messages/, title: 'Messages' },
  { match: /^\/contacts/, title: 'Contacts' },
  { match: /^\/revenue/, title: 'Revenue' },
  { match: /^\/settings/, title: 'Settings' },
]

const MENU_SECTIONS = [
  {
    label: 'Comms',
    items: [
      { label: 'Contacts', to: '/contacts' },
      { label: 'Calendar', to: '/calendar' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Outreach', to: '/outreach' },
      { label: 'Portal access', to: '/portal-access' },
      { label: 'Audience', to: '/audience' },
      { label: 'Signals', to: '/signals' },
      { label: 'Tasks', to: '/tasks' },
      { label: 'Compliance', to: '/compliance' },
      { label: 'Prospects', to: '/prospects' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { label: 'Contracts', to: '/contracts' },
      { label: 'Revenue & fees', to: '/revenue' },
      { label: 'Invoices', to: '/invoices' },
      { label: 'Payments', to: '/payments' },
    ],
  },
  {
    label: 'Account',
    items: [
      { label: 'Settings', to: '/settings' },
      { label: 'Team', to: '/team' },
      { label: 'Help', to: '/help' },
    ],
  },
]

function screenTitle(pathname: string): string {
  return SCREEN_TITLES.find(s => s.match.test(pathname))?.title ?? 'Reppr'
}

export default function MobileHeader() {
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  function go(to: string) {
    setMenuOpen(false)
    navigate(to)
  }

  return (
    <>
      <header className={styles.header}>
        {/* The shared icon asset, not a hand-drawn path: the mark is R+P, and
            the design handoff's inline SVG mis-drew it as a double P. One
            source of truth keeps every surface on the same logo. */}
        <img src="/icon-192.png" alt="" width={22} height={22} className={styles.mark} />
        <h1 className={styles.title}>{screenTitle(pathname)}</h1>
        <button
          className={styles.menuBtn}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="More screens"
          aria-expanded={menuOpen}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </header>

      {menuOpen && (
        <>
          <div className={styles.overlay} onClick={() => setMenuOpen(false)} />
          <div className={styles.sheet} role="dialog" aria-label="More screens">
            <div className={styles.sheetHandle} />
            <div className={styles.sheetContent}>
              {MENU_SECTIONS.map(section => (
                <div key={section.label} className={styles.sheetSection}>
                  <div className={styles.sheetSectionLabel}>{section.label}</div>
                  <div className={styles.sheetGrid}>
                    {section.items.map(item => (
                      <button key={item.to} className={styles.sheetItem} onClick={() => go(item.to)}>
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  )
}
